import {
  type ApolloCache,
  type ApolloClient,
  type ApolloLink,
  ApolloProvider,
  type DefaultOptions,
  type NormalizedCacheObject,
  type TypePolicies,
} from "@apollo/client";
import type { FragmentRegistryAPI } from "@apollo/client/cache";
import { useMemo } from "react";
import { Platform } from "react-native";

import type { FetchTrackedOneGraphOperation } from "./ApolloNativeModule";
import { createOneGraphApolloClient } from "./createOneGraphApolloClient";

export interface PlatformOptions {
  web?: {
    graphqlUrl?: string;
    graphqlApiKey?: string;
  };
}

export interface Props {
  children?: React.ReactNode;
  fetch: FetchTrackedOneGraphOperation;
  rootTrackingKey?: string;
  defaultOptions?: DefaultOptions;
  cache?: ApolloCache<NormalizedCacheObject>;
  platformOptions?: PlatformOptions;
  typePolicies?: TypePolicies;
  errorLink?: ApolloLink;
  prforceGroups?: string;
  fragments?: FragmentRegistryAPI;
  placementId?: string;
  /**
   * Externally-created client to use instead of creating one internally.
   * When provided (e.g. an SSR client supplied by the render entry), the
   * provider skips creating its own client.
   */
  externalClient?: ApolloClient<NormalizedCacheObject>;
  country?: string;
  /** Web only. CTK sent with OneGraph requests for Proctor assignment. */
  ctk?: string;
  /**
   * Web only. When false, the created client does not rehydrate from
   * `window.__APOLLO_STATE__`. Defaults to true.
   */
  restoreWindowApolloState?: boolean;
}

export function OneGraphApolloClientProvider(props: Props): React.ReactNode {
  const {
    children,
    fetch,
    rootTrackingKey,
    defaultOptions,
    cache,
    platformOptions,
    typePolicies,
    errorLink,
    prforceGroups,
    fragments,
    placementId,
    externalClient,
    country,
    ctk,
    restoreWindowApolloState,
  } = props;
  const webCtk = Platform.OS === "web" ? ctk : undefined;

  const createdClient = useMemo(() => {
    if (externalClient) return null;
    return createOneGraphApolloClient({
      fetch,
      trackingKey: rootTrackingKey,
      defaultOptions,
      cache,
      platformOptions,
      typePolicies,
      errorLink,
      prforceGroups,
      fragments,
      placementId,
      country,
      ...(webCtk ? { ctk: webCtk } : {}),
      restoreWindowApolloState,
    });
  }, [
    externalClient,
    rootTrackingKey,
    defaultOptions,
    cache,
    platformOptions,
    fetch,
    typePolicies,
    errorLink,
    prforceGroups,
    fragments,
    placementId,
    country,
    webCtk,
    restoreWindowApolloState,
  ]);

  const client = externalClient ?? createdClient!;
  return <ApolloProvider client={client}>{children}</ApolloProvider>;
}
